import { useCallback, useEffect, useState } from 'react';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { getNetconfEventPreview } from '@api/netconfEvents';

import { handleErrorFromMessage } from '@utils/handleRequestError';
import { parseRawConfig } from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/utils';

import { IId } from '@interfaces/common/common';
import { IResponseError } from '@interfaces/errors/apiErrors';
import { INetconfEventPreview } from '@interfaces/vesData/netconfEvents';
import { TMapTree } from '@components/common/FlatXMLTree/interface';

import { EUserRoles } from '@enums/common';

interface IUseQueryNetconfRawDataByIdReturnType {
	netconfEventParsedData?: TMapTree | null;
	isLoadingNetconfEventData: boolean;
	netconfData?: INetconfEventPreview;
}

export const useQueryNetconfPreviewDataById = (params: IId): IUseQueryNetconfRawDataByIdReturnType => {
	const { id } = params;

	const { user } = useAppSelector(auth);

	const userRoles = user?.roles || [];

	const isAllowedRoles = [EUserRoles.smoLightUpdater, EUserRoles.smoLightViewer]
		.every((role) => userRoles.includes(role));

	const [netconfEventParsedData, setNetconfEventParsedData] = useState<TMapTree | null>(null);
	const [isLoadingNetconfEventData, setIsLoadingNetconfEventData] = useState<boolean>(true);
	const [netconfData, setNetconfData] = useState<INetconfEventPreview | undefined>(undefined);

	const getData = useCallback(async () => {
		try {
			setIsLoadingNetconfEventData(true);

			const { text, ...rest } = await getNetconfEventPreview({ id });

			if (!rest.isXml) {
				const result = {
					...rest,
					text,
					tree: null,
				};

				setNetconfData(result);
				setNetconfEventParsedData(null);
				setIsLoadingNetconfEventData(false);
				return;
			}

			const worker = new Worker(
				new URL('../../components/routes/AdminPanel/ManageNetworkElementConfigurations/parseWorker',
					import.meta.url,
				));

			const tree = await parseRawConfig(
				text,
				worker,
			);

			worker.terminate();

			const result = {
				...rest,
				text,
				tree,
			};

			setNetconfData(result);
			setNetconfEventParsedData(tree);
			setIsLoadingNetconfEventData(false);
		} catch (error) {
			setIsLoadingNetconfEventData(false);
			handleErrorFromMessage(error as IResponseError);
		}
	}, [id]);

	useEffect(() => {
		if (!id && isAllowedRoles) {
			return;
		}

		getData();
	}, [id, getData, isAllowedRoles]);

	return {
		netconfEventParsedData,
		isLoadingNetconfEventData,
		netconfData,
	};
};
